import { ActorRef, DoneInvokeEvent } from 'xstate';
import { RefObject } from 'react';
import { IViews } from './configInterface';

export const enum SPEED_TEST_LEVEL {
  LOW,
  MEDIUM,
  HIGH,
}
export type mediaDeviceType = 'video' | 'audio';

type ConstrainProps = {
  video: {
    width: {
      min?: number;
      ideal?: number;
      max: number;
    };
    aspectRatio?: number;
  };
  audio: boolean;
};

interface FacingMode {
  exact: string;
}
export interface RecorderInterface {
  recordWithoutVideo?: boolean;
  facingMode: FacingMode;
  microphoneRef: ActorRef<DoneInvokeEvent<any>> | null;
  countDownRef: ActorRef<DoneInvokeEvent<any>> | null;
  mediaStream?: MediaStream;
  mediaRecorder?: MediaRecorder;
  videoDevices: MediaDeviceInfo[];
  audioDevices: MediaDeviceInfo[];
  selectedVideoDevice?: MediaDeviceInfo;
  selectedAudioDevice?: MediaDeviceInfo;
  mimeType: string;
  mimeTypes: string[];
  isMobile: boolean;
  isSafeMode: boolean;
  isAutoStart: boolean;
  speedTestLevel: SPEED_TEST_LEVEL;
  constraint: ConstrainProps;
  videoRef: RefObject<HTMLVideoElement>;
  videoBitsPerSecond: number;
  countdown: number;
  recordingTime: number;
  durations: number;
  isMicError: boolean;
  isActivelyStopped: boolean;
  forcedView?: IViews;
}
export const enum MIME_TYPES {
  WEBM = 'video/webm',
  MP4 = 'video/mp4',
}
export const enum STATES {
  INIT = 'init',
  IDLE = 'idle',
  RECORDING = 'recording',
  WAITING = 'waiting',
  ERROR = 'error',
  INIT_RECORDER = 'initRecorder',
  COUNT_DOWN = 'countDown',
  COLLECTING_BLOBS = 'collectingBlobs',
  RECORDER_TIMER = 'recorderTimer',
  RECORDER_SENDING_BLOBS = 'recorderSendingBlobs',
}
export const enum ACTIONS {
  STOP_COUNT_DOWN_ACTOR = 'STOP_COUNT_DOWN_ACTOR',
  INIT_COUNT_DOWN_ACTOR = 'INIT_COUNT_DOWN_ACTOR',
  SESSION_EVENT = 'SESSION_EVENT',
  SENTRY = 'SENTRY',
  CONSOLE_LOG = 'CONSOLE_LOG',
  REMOVE_MIC_ERROR = 'REMOVE_MIC_ERROR',
  PAUSE_MEDIA_RECORDER = 'PAUSE_MEDIA_RECORDER',
  RESUME_MEDIA_RECORDER = 'RESUME_MEDIA_RECORDER',
  SEND_CURRENT_TAKE = 'SEND_CURRENT_TAKE',
  STOP_MICROPHONE_MACHINE = 'STOP_MICROPHONE_MACHINE',
  STOP_MEDIA_RECORDER = 'STOP_MEDIA_RECORDER',
  DISABLED_AUTO_START = 'DISABLED_AUTO_START',
  SET_AUTO_START = 'SET_AUTO_START',
  UPDATE_RECORDING_TIME = 'UPDATE_RECORDING_TIME',
  UPDATE_COUNT_DOWN = 'UPDATE_COUNT_DOWN',
  SET_MIC_MACHINE = 'INIT_MIC_MACHINE',
  SEND_MEDIA_STREAM_TO_MIC_MACHINE = 'SET_MEDIA_STREAM_TO_MIC_MACHINE',
  SET_MEDIA_STREAM_CORE = 'SET_MEDIA_STREAM_CORE',
  STOP_MEDIA_STREAM = 'STOP_MEDIA_STREAM',
  SET_MIME_TYPE = 'SET_MIME_TYPE',
  SET_SAFE_MODE = 'SET_SAFE_MODE',
  SEND_ERROR_TO_PARENT = 'SEND_ERROR_TO_PARENT',
  SEND_SUCCESS_TO_PARENT = 'SEND_SUCCESS_TO_PARENT',
  CHANGE_VIDEO_DEVICE = 'CHANGE_VIDEO_DEVICE',
  CHANGE_AUDIO_DEVICE = 'CHANGE_AUDIO_DEVICE',
  SET_QUESTION_PARAMS = 'SET_QUESTION_PARAMS',
  UPDATE_CONSTRAINT = 'UPDATE_CONSTRAINT',
  UPDATE_AUDIO_AND_VIDEO_DEVICES = 'UPDATE_AUDIO_AND_VIDEO_DEVICES',
  SET_FORCE_START = 'SET_FORCE_START',
  RESET_MACHINE = 'RESET_MACHINE',
  UPDATE_MACHINE_FOR_NEW_QUESTION = 'UPDATE_MACHINE_FOR_NEW_QUESTION',
  SET_MEDIA_RECORDER = 'SET_MEDIA_RECORDER',
  SET_MIC_ERROR = 'SET_MIC_ERROR',
  SET_PERMISSION_LISTENER = 'SET_PERMISSION_LISTENER',
  SET_ACTIVELY_STOPPED = 'SET_ACTIVELY_STOPPED',
}
export const enum EVENTS {
  SET_MIC_SPEED_DETECTION = 'SET_MIC_SPEED_DETECTION',
  SEND_RECORDER_TO_WAITING_STATE = 'SEND_RECORDER_TO_WAITING_STATE',
  CHANGE_VIDEO_DEVICE = 'CHANGE_VIDEO_DEVICE',
  CHANGE_AUDIO_DEVICE = 'CHANGE_AUDIO_DEVICE',
  START_RECORDING = 'START_RECORDING',
  SET_QUESTION_PARAMS = 'SET_QUESTION_PARAMS',
  SPEED_TEST_RESULT_UPDATED = 'SPEED_TEST_RESULT_UPDATED',
  RECORDER_DEVICE_ERROR = 'RECORDER_DEVICE_ERROR',
  UPDATE_DEVICES_LIST = 'UPDATE_DEVICES_LIST',
  UPDATE_CURRENT_DEVICE = 'UPDATE_CURRENT_DEVICE',
  RECORDING_UPDATE_TIMER = 'RECORDING_UPDATE_TIMER',
  UPDATE_TIMER = 'UPDATE_TIMER',
  FORCE_START = 'FORCE_START',
  STOP_RECORDING = 'STOP_RECORDING',
  SET_MEDIA_RECORDER = 'SET_MEDIA_RECORDER',
  SEND_CURRENT_TAKE = 'SEND_CURRENT_TAKE',
  RE_INIT = 'RE_INIT',
  RETRY = 'RETRY',
  MIC_DATA_RECEIVED = 'MIC_DATA_RECEIVED',
  PAUSE_MEDIA_RECORDER = 'PAUSE_MEDIA_RECORDER',
}
export const enum SERVICES {
  GET_MEDIA_STREAM = 'getMediaStream',
  GET_MEDIA_RECORDER = 'getMediaRecorder',
}
export const enum GUARDS {
  IS_TIME_TO_STOP_RECORDING = 'isTimeToStopRecording',
  IS_RECORDER_ACTIVE = 'isRecorderActive',
  IS_TIME_TO_START_RECORDING = 'isTimeToStartRecording',
  NO_SOUND_FROM_MIC_MACHINE = 'noSoundFromMicMachine',
  IS_NOT_ALLOWED_ERROR = 'isNotAllowedError',
  IS_ERROR_WITH_WRONG_PARAMS = 'isErrorWithWrongParams',
  IS_AUTO_START = 'isAutoStart',
  IS_QUALITY_CHANGED = 'isQualityChanged',
  IS_VIDEO_CORRUPTED = 'isVideoCorrupted',
}
export const mediaConstrains = {
  hd: {
    video: {
      width: { min: 640, ideal: 1280, max: 1280 },
    },
    audio: true,
  },
  vga: {
    video: {
      width: { max: 640, ideal: 640 },
    },
    audio: true,
  },
  qvga: {
    video: {
      width: { min: 320, ideal: 320, max: 480 },
      aspectRatio: 4 / 3,
    },
    audio: true,
  },
  mobile: {
    video: {
      width: { min: 480, ideal: 680, max: 1280 },
      aspectRatio: 4 / 3,
    },
    audio: true,
  },
  mobile2: {
    video: {
      width: { min: 480, ideal: 620, max: 1280 },
      aspectRatio: 4 / 3,
    },
    audio: true,
  },
  mobile3: {
    video: {
      width: { min: 480, ideal: 480, max: 480 },
      aspectRatio: 4 / 3,
    },
    audio: true,
  },
  noVideo: { video: false, audio: true },
};
